import { Paper, Typography } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import { getScoreMeta } from "../constants";

interface LeadScoreDistributionChartProps {
  data: LeadsManagementData;
}

function rangeLowerBound(range: string): number {
  return Number(range.split("-")[0]) || 0;
}

export default function LeadScoreDistributionChart({ data }: LeadScoreDistributionChartProps) {
  const { aggregations, goToFilteredLeads } = data;

  // The 4 tiers (8-10/5-7/3-4/1-2) and their counts are computed server-side
  // over the full filtered result set — see aggregations.lead_score_distribution
  // in the brand_leads_report/ response spec.
  const bands = [...aggregations.leadScoreDistribution]
    .sort((a, b) => rangeLowerBound(a.range) - rangeLowerBound(b.range))
    .map((band) => ({
      label: band.range,
      min: rangeLowerBound(band.range),
      count: band.count,
      color: getScoreMeta(rangeLowerBound(band.range)).color,
    }));

  return (
    <Paper variant="outlined" data-tracker="968" sx={{ p: "20px 24px", borderRadius: 2 }}>
      <Typography sx={{ fontSize: 18, fontWeight: 500, color: "#1a1a1a", mb: 0.5 }}>
        Lead Score Distribution
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: "#9e9e9e", mb: 2 }}>
        Click a band to filter the leads list by minimum score.
      </Typography>
      <ResponsiveContainer width="100%" height={190}>
        <BarChart data={bands} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
          <CartesianGrid stroke="#eeeeee" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#757575" }} />
          <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#9e9e9e" }} />
          <RechartsTooltip />
          <Bar
            dataKey="count"
            radius={[4, 4, 0, 0]}
            cursor="pointer"
            onClick={(barProps: any) => goToFilteredLeads({ minScore: barProps.payload.min })}
          >
            {bands.map((band) => (
              <Cell key={band.label} fill={band.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
}
